// utils/carteleriaFacturacion.js
//
// Lógica PURA (sin DB) de la ventana de facturación de un contrato de cartelería.
// Única definición del "fin" del contrato y de "hasta qué mes se factura mientras
// sigue activo". Centralizado acá (regla 10.1) para que TODOS los consumidores
// —VentasFacturas hoy; ERv2 / cobros / VentasProyeccion en fases siguientes— usen
// la misma noción y no diverjan (antecedente: reporte de #14 con 3 criterios distintos).

// Estados de cartelería que cuentan como "el contrato sigue vivo → se sigue facturando".
// DEBE alinearse con el filtro de elegibilidad de VentasFacturas (useVFDataLoading.js).
const ESTADOS_ACTIVOS_FACTURABLES = ["activo", "100%"];

export const esEstadoActivoFacturable = (estado) => ESTADOS_ACTIVOS_FACTURABLES.includes((estado || "").toLowerCase());

// Mes/año de FIN del PLAZO firmado = fecha_inicio + plazo_contrato_meses - 1 (último
// mes de servicio del plazo). Devuelve { year, month } (month 1-indexado) o null.
export const getFinContrato = (fechaInicio, plazoMeses) => {
  if (!fechaInicio || !plazoMeses) return null;
  const [yearStr, monthStr] = fechaInicio.split("-");
  let year = parseInt(yearStr, 10);
  let month = parseInt(monthStr, 10);
  const meses = parseInt(plazoMeses, 10);
  const nuevoMes = month + meses - 1;
  year += Math.floor(nuevoMes / 12);
  month = nuevoMes % 12;
  if (month === 0) {
    month = 12;
    year -= 1;
  }
  return { year, month };
};

// ¿El plazo firmado ya venció (independiente del estado)? Compara el fin del plazo
// contra el mes actual (`hoy`, inyectable para tests).
export const isContratoVencido = (fechaInicio, plazoMeses, hoy = new Date()) => {
  const fin = getFinContrato(fechaInicio, plazoMeses);
  if (!fin) return false;
  const currentYear = hoy.getFullYear();
  const currentMonth = hoy.getMonth() + 1;
  return fin.year < currentYear || (fin.year === currentYear && fin.month < currentMonth);
};

// Meses corridos pasados el fin del plazo (prórroga de hecho). Puramente derivado:
// NO altera el plazo firmado. Solo tiene sentido cuando ya está vencido.
export const getMesesExtendidos = (fechaInicio, plazoMeses, hoy = new Date()) => {
  const fin = getFinContrato(fechaInicio, plazoMeses);
  if (!fin) return 0;
  const currentYear = hoy.getFullYear();
  const currentMonth = hoy.getMonth() + 1;
  return (currentYear - fin.year) * 12 + (currentMonth - fin.month);
};

// Nº de cuotas mensuales a GENERAR desde fecha_inicio para un contrato.
//
// Regla de negocio (2026-08-11): el corte de facturación de un contrato de cartelería
// ya NO es la fecha fin del plazo, sino su ESTADO. Mientras siga en estado activo
// (aunque el plazo haya vencido) se le sigue dando servicio y facturando → sus cuotas
// deben generarse hasta el MES ACTUAL inclusive. Cuando el estado deja de ser activo
// (Cumplido / terminal) o se renueva, la ventana vuelve a ser el plazo firmado.
//
//   - estado activo:      max(plazo, meses desde fecha_inicio hasta el mes actual incl.)
//   - estado NO activo:   plazo (comportamiento histórico, sin extensión)
//
// `hoy` es inyectable para tests. Devuelve el CONTEO de meses (i corre 0..N-1 desde
// el mes de fecha_inicio), consistente con getFinContrato (plazo == meses hasta el fin).
export const getMesesAFacturar = (fechaInicio, plazoMeses, estado, hoy = new Date()) => {
  const plazo = parseInt(plazoMeses, 10) || 0;
  if (!fechaInicio || plazo === 0) return plazo;
  if (!esEstadoActivoFacturable(estado)) return plazo;

  const [yearStr, monthStr] = fechaInicio.split("-");
  const startYear = parseInt(yearStr, 10);
  const startMonth = parseInt(monthStr, 10); // 1-indexado
  const currentYear = hoy.getFullYear();
  const currentMonth = hoy.getMonth() + 1; // 1-indexado

  // Meses desde el mes de inicio hasta el mes actual, inclusive.
  const mesesHastaMesActual = (currentYear - startYear) * 12 + (currentMonth - startMonth) + 1;
  return Math.max(plazo, mesesHastaMesActual);
};
